import React, { useState, useEffect, useCallback, useRef } from "react";
import "../styles/guiaSocios.css";
import { FaSearch } from "react-icons/fa";
import api from "../services/api";

const formatFecha = (isoDate) => {
  const fecha = new Date(isoDate);
  return fecha.toLocaleDateString("es-BO", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

function GuiaSocios() {
  const [socios, setSocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const sociosPorPagina = 5;

  const [filterType, setFilterType] = useState("Nombre");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedSocioIndex, setSelectedSocioIndex] = useState(0);
  const didMountSearchRef = useRef(false);

  const fetchSocios = useCallback(async (page = 1, search = "", filter = "Nombre") => {
    try {
      setLoading(true);
      const response = await api.get("/socios/", {
        params: {
          page,
          page_size: sociosPorPagina,
          search: search || undefined,
          search_type: filter === "Especialidad" ? "especialidad" : "general",
        },
      });

      const pageResults = (response.data.results || []).map((s) => ({
        id: s.id,
        nombres: s.nombres,
        apellidos: s.apellidos,
        especialidad: s.especialidad,
        avatar: s.avatar,
        avatar_url: s.avatar_url,
        fecha_ins: formatFecha(s.fecha_ins),
        universidad: s.universidad,
        nreg_profesional: s.nreg_profesional,
        reg_abis: s.reg_abis,
      }));

      setSocios(pageResults);
      setTotalCount(response.data.count || 0);
      setNextPage(response.data.next);
      setPrevPage(response.data.previous);
      setCurrentPage(page);
      setError(null);
    } catch (err) {
      setError("Error al cargar socios: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [sociosPorPagina]);

  useEffect(() => {
    fetchSocios(1);
  }, [fetchSocios]);

  useEffect(() => {
    if (!didMountSearchRef.current) {
      didMountSearchRef.current = true;
      return;
    }

    const timer = setTimeout(() => {
      const term = searchTerm.trim();
      setAppliedSearchTerm(term);
      fetchSocios(1, term, filterType);
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm, filterType, fetchSocios]);

  const totalPages = Math.max(1, Math.ceil(totalCount / sociosPorPagina));

  // 🔹 Filtro
  const handleFilter = () => {
    const term = searchTerm.trim();
    setAppliedSearchTerm(term);
    fetchSocios(1, term, filterType);
  };

  // 🔹 Navegación con teclas
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showModal) return;
      if (e.key === "Escape") setShowModal(false);
      if (!socios.length) return;
      if (e.key === "ArrowRight")
        setSelectedSocioIndex((prev) => (prev + 1) % socios.length);
      if (e.key === "ArrowLeft")
        setSelectedSocioIndex((prev) =>
          prev === 0 ? socios.length - 1 : prev - 1
        );
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showModal, socios.length]);

  const socio = socios[selectedSocioIndex];

  return (
    <div className="guia-container">
      <h1 className="guia-title">Guía de Socios</h1>

      <div className="filter-container">
        <div className="filter-buttons">
          {["Nombre", "Especialidad"].map((type) => (
            <button
              key={type}
              className={`filter-btn ${
                filterType === type ? "active-filter" : ""
              }`}
              onClick={() => setFilterType(type)}
            >
              {type}
            </button>
          ))}
        </div>
        <div className="search-box">
          <input
            type="text"
            placeholder={`Buscar por ${filterType.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleFilter();
              }
            }}
          />
          <button className="search-btn" onClick={handleFilter}>
            <FaSearch />
          </button>
        </div>
      </div>
      {loading && <p>Cargando socios...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {!loading && totalCount === 0 && <p>No hay socios registrados.</p>}

      <div className="table-wrapper">
        <table className="socios-table">
          <thead>
            <tr>
              <th>N°</th>
              <th>Nombre y Apellido</th>
              <th>Especialidad</th>
              <th>Registro Profesional</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {socios.map((s, index) => (
              <tr key={index}>
                <td>{(currentPage - 1) * sociosPorPagina + index + 1}</td>
                <td>{`${s.nombres} ${s.apellidos}`}</td>
                <td>{s.especialidad}</td>
                <td>{s.nreg_profesional}</td>
                <td>
                  <button
                    className="ver-btn"
                    onClick={() => {
                      setSelectedSocioIndex(index);
                      setShowModal(true);
                    }}
                  >
                    Ver
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="socios-cards">
        {socios.map((s, index) => (
          <div key={`card-${index}`} className="socio-card">
            <div className="socio-card-numero">
              {(currentPage - 1) * sociosPorPagina + index + 1}
            </div>
            <div className="socio-card-info">
              <p className="socio-card-nombre">
                {s.nombres} {s.apellidos}
              </p>
              <p className="socio-card-especialidad">
                {s.especialidad || "—"}
              </p>
            </div>
            <button
              className="socio-card-ver"
              onClick={() => {
                setSelectedSocioIndex(index);
                setShowModal(true);
              }}
            >
              Ver
            </button>
          </div>
        ))}
      </div>

      {/* 🔹 Paginación */}
      <div className="pagination">
        <button
          onClick={() =>
            fetchSocios(Math.max(currentPage - 1, 1), appliedSearchTerm, filterType)
          }
          disabled={!prevPage || currentPage === 1}
        >
          Anterior
        </button>
        <span>
          Página {currentPage} de {totalPages}
        </span>
        <button
          onClick={() =>
            fetchSocios(currentPage + 1, appliedSearchTerm, filterType)
          }
          disabled={!nextPage}
        >
          Siguiente
        </button>
      </div>

      {/* 🔹 Modal */}
      {showModal && socio && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button className="close-btn" onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-avatar-wrapper">
              <img
                src={socio.avatar_url}
                alt={`${socio.nombres} ${socio.apellidos}`}
                className="modal-avatar"
              />
            </div>

            <div className="modal-name">
              <h2>
                {socio.nombres} {socio.apellidos}
              </h2>
              <span className="modal-badge">Socio Registrado</span>
            </div>

            <div className="modal-body">
              <div className="modal-field">
                <span className="modal-label">Registro ABIS</span>
                <span className="modal-value">{socio.reg_abis || "—"}</span>
              </div>
              <div className="modal-field">
                <span className="modal-label">Registro Profesional</span>
                <span className="modal-value">
                  {socio.nreg_profesional || "—"}
                </span>
              </div>
              <div className="modal-field">
                <span className="modal-label">Fecha de Registro</span>
                <span className="modal-value">{socio.fecha_ins || "—"}</span>
              </div>
              <div className="modal-field">
                <span className="modal-label">Universidad</span>
                <span className="modal-value">{socio.universidad || "—"}</span>
              </div>
            </div>

            <div className="modal-disclaimer">
              <p>
                <strong>IMPORTANTE:</strong> La información contenida en la
                presente Guía Electrónica no constituye una certificación
                profesional ni acredita legalmente a los profesionales
                Ingenieros Registrados ante terceras personas, Instituciones o
                Empresas Privadas. Las Certificaciones Oficiales deberán ser
                solicitadas en oficinas de la SIB Nacional o en las Oficinas
                Departamentales.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GuiaSocios;
